import { createContext, useEffect, useState, ReactNode } from "react";
import { useRouter } from "next/router";
import { canUseFeature, Feature } from "@memento/library/feature-access";
import { useFeatureFlags } from "src/contexts/FeatureFlagProvider";
import { useViewport } from "src/hooks/useViewport";
import { useSubscription } from "src/hooks/useSubscription";
import { pushGoogletagCommand, refresh } from "./AdsManager";

const DEFAULT_TARGETING_ATTRIBUTES: ads.TargetingAttributes = {
  SITE: "MEMENTO",
};

export const AdsContext = createContext<ads.Configuration>({
  enabled: true,
  setEnabled: () => {},
  targetingAttributes: {},
  setTargetingAttributes: () => {},
});

const usePageTargeting = () => {
  const [targetingAttributes, setTargetingAttributes] =
    useState<ads.TargetingAttributes>(DEFAULT_TARGETING_ATTRIBUTES);

  useEffect(() => {
    pushGoogletagCommand((googletag) => {
      const pubads = googletag.pubads();
      pubads.clearTargeting();

      const allPageTargeting: ads.TargetingAttributes = {
        ...DEFAULT_TARGETING_ATTRIBUTES,
        ...targetingAttributes,
      };

      Object.entries(allPageTargeting).forEach(([key, value]) => {
        pubads.setTargeting(key.toUpperCase(), value.toUpperCase());
      });
    });
  }, [targetingAttributes]);

  return {
    targetingAttributes,
    setTargetingAttributes: (attrs: ads.TargetingAttributes | null) =>
      setTargetingAttributes(attrs || DEFAULT_TARGETING_ATTRIBUTES),
  };
};

export const AdSlotsProvider = ({ children }: { children: ReactNode }) => {
  const { subscription } = useSubscription();
  const { featureFlags } = useFeatureFlags();
  const { targetingAttributes, setTargetingAttributes } = usePageTargeting();

  const [enabled, setEnabled] = useState(true);
  const { isMobile } = useViewport();

  const config: ads.Configuration = {
    enabled,
    setEnabled,
    targetingAttributes,
    setTargetingAttributes,
  };

  // Refresh ads on location changes.
  const router = useRouter();
  useEffect(() => {
    refresh();
  }, [router.pathname]);

  // Trigger ads refresh when isMobile changes.
  useEffect(() => {
    refresh();
  }, [isMobile]);

  // Make sure to never show ads if the user has the right subscription (even
  // if they are turned on somewhere)
  useEffect(() => {
    // If ads are already off, no need to proceed
    if (!enabled) {
      return;
    }

    // Ads are enabled by default, so only disable them here if the user
    // subscription warrants it. Other resources (e.g. card, collaborators) will
    // be taken into account elsewhere (e.g. useAds)
    const isRemoveAds = canUseFeature(
      Feature.GroupVideosRemoveAds,
      subscription,
      {
        featureFlags,
      }
    );

    if (isRemoveAds) {
      setEnabled(false);
    }
  }, [enabled, subscription, featureFlags]);

  return <AdsContext.Provider value={config}>{children}</AdsContext.Provider>;
};

export default AdSlotsProvider;
